import React from 'react';
import { Box, Collapse } from '@mui/material';
import { Alert, Button, DocumentDropzone, DropzoneErrorCode, Modal, ModalActions, ModalContent, ModalTitle } from '@uplift/ui-v2';
import { ProviderDocumentDetailsForm } from './ProviderDocumentDetailsForm';


export type UploadProviderDocumentModalProps = {
  providerId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
};


export const UploadProviderDocumentModal: React.VFC<UploadProviderDocumentModalProps> = (props) => {
  const [ error, setError ] = React.useState<DropzoneErrorCode | null>(null);
  const [ file, setFile ] = React.useState<File | null>(null);

  const content = React.useMemo(() => {
    if (!file) {
      const errorMessageByCode: Partial<Record<DropzoneErrorCode, string>> = {
        [DropzoneErrorCode.FileTooLarge]: 'Files must be less than 10MB. Try reducing the file size or choose another file to upload.',
        [DropzoneErrorCode.FileInvalidType]: 'Only PDFs can be uploaded. Try converting the document to a PDF or choose another file to upload.',
        [DropzoneErrorCode.TooManyFiles]: 'Only one document can be uploaded at once.',
      };

      return (
        <>
          <ModalTitle>Select document to upload</ModalTitle>
          <ModalContent>
            <Collapse in={!!error}>
              <Box pb={2}>
                <Alert severity="error">
                  {error ? errorMessageByCode[error] : error}
                </Alert>
              </Box>
            </Collapse>
            <Box height="280px">
              <DocumentDropzone
                onFiles={files => {
                  setError(null);
                  setFile(files[0]);
                }}
                onError={setError}
              />
            </Box>
          </ModalContent>
          <ModalActions>
            <Button onClick={props.onClose}>Cancel</Button>
          </ModalActions>
        </>
      );
    }

    return (
      <ProviderDocumentDetailsForm
        providerId={props.providerId}
        file={file}
        onCancel={() => setFile(null)}
        onSuccess={props.onSuccess}
      />
    );
  }, [ file, error, props.onClose, props.onSuccess ]);

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth="md"
      showCloseIcon={false}
      slotProps={{
        paper: { style: { maxWidth: '600px' } },
        transition: {
          onExited: () => {
            setFile(null);
            setError(null);
          }
        }
      }}
    >
      {content}
    </Modal>
  );
};
